import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Eye, 
  Users,
  User,
  Building,
  Truck,
  Mail,
  Phone,
  Calendar,
  Key,
  Settings,
  X,
  MapPin
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PersonDetailsModal from '../../components/modals/PersonDetailsModal';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import { asaasHelpers } from '../../lib/asaasHelpers';
import toast from 'react-hot-toast';

const PeoplePage = () => {
  const location = useLocation();
  const { company } = useAuth();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [syncingAsaas, setSyncingAsaas] = useState(false);

  const personTypes = [
    { value: 'user', label: 'Usuário' },
    { value: 'customer', label: 'Cliente' },
    { value: 'supplier', label: 'Fornecedor' },
    { value: 'student', label: 'Aluno' },
    { value: 'parent', label: 'Responsável' },
    { value: 'employee', label: 'Funcionário' }
  ];

  useEffect(() => {
    if (company?.id) {
      loadPeople();
    }
  }, [company]);

  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const loadPeople = async () => {
    try {
      setLoading(true);
      const data = await dataHelpers.getPeople(company.id);
      setPeople(data || []);
    } catch (error) {
      console.error('Error loading people:', error);
      toast.error('Erro ao carregar pessoas');
      setPeople([]);
    } finally {
      setLoading(false);
    }
  };
  const handleDeletePerson = async (person) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${person.name}"?`)) {
      return;
    }

    try {
      await dataHelpers.deletePerson(person.id);
      setPeople(prev => prev.filter(p => p.id !== person.id));
      toast.success('Pessoa excluída com sucesso');
    } catch (error) {
      console.error('Error deleting person:', error);
      toast.error('Erro ao excluir pessoa');
    }
  };

  const handleSyncAsaas = async (person) => {
    if (!company?.id) {
      toast.error('Empresa não encontrada');
      return;
    }

    try {
      setSyncingAsaas(true);
      
      const syncResult = await asaasHelpers.syncCustomer(person.id, company.id, true);
      
      if (syncResult.success) {
        if (syncResult.already_synced) {
          toast.success('Cliente atualizado no Asaas com sucesso!');
        } else {
          toast.success('Cliente criado no Asaas com sucesso!');
        }
        
        // Atualizar os dados da pessoa na lista
        setPeople(prev => prev.map(p => 
          p.id === person.id 
            ? { 
                ...p, 
                asaas_customer_id: syncResult.customer_id,
                asaas_sync_status: 'synced'
              }
            : p
        ));
        
        // Atualizar pessoa selecionada se for a mesma
        if (selectedPerson?.id === person.id) {
          setSelectedPerson(prev => ({
            ...prev,
            asaas_customer_id: syncResult.customer_id,
            asaas_sync_status: 'synced'
          }));
        }
      } else {
        toast.error(`Erro na sincronização: ${syncResult.error}`);
      }
    } catch (error) {
      console.error('Erro ao sincronizar com Asaas:', error);
      toast.error('Erro ao sincronizar com Asaas');
    } finally {
      setSyncingAsaas(false);
    }
  };

  const handleShowDetails = (person) => {
    setSelectedPerson(person);
    setShowDetailsModal(true);
  };

  const handlePersonUpdate = (updatedPerson) => {
    // Atualizar a pessoa na lista
    setPeople(prev => prev.map(p => 
      p.id === updatedPerson.id ? updatedPerson : p
    ));
    
    // Atualizar a pessoa selecionada
    setSelectedPerson(updatedPerson);
  };

  const handleTogglePersonStatus = async (person) => {
    try {
      const newStatus = !person.active;
      await dataHelpers.updatePerson(person.id, { active: newStatus });
      
      setPeople(prev => prev.map(p => 
        p.id === person.id ? { ...p, active: newStatus } : p
      ));
      
      toast.success(`Pessoa ${newStatus ? 'ativada' : 'desativada'} com sucesso`);
    } catch (error) {
      console.error('Error updating person status:', error);
      toast.error('Erro ao atualizar status da pessoa');
    }
  };

  const filteredPeople = people.filter(person => {
    const matchesSearch = person.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         person.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         person.phone?.includes(searchTerm.replace(/\D/g, '')) ||
                         person.document?.includes(searchTerm.replace(/\D/g, ''));
    
    const matchesType = !selectedType || person.type === selectedType;
    
    const matchesStatus = !selectedStatus || 
      (selectedStatus === 'active' && person.active) ||
      (selectedStatus === 'inactive' && !person.active);
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: people.length,
    active: people.filter(p => p.active).length,
    inactive: people.filter(p => !p.active).length,
    users: people.filter(p => p.type === 'user').length,
    customers: people.filter(p => p.type === 'customer').length,
    suppliers: people.filter(p => p.type === 'supplier').length,
    students: people.filter(p => p.type === 'student').length,
    employees: people.filter(p => p.type === 'employee').length
  };

  const getPersonTypeLabel = (type) => {
    const typeObj = personTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };

  const getPersonTypeIcon = (type) => {
    switch (type) {
      case 'user':
        return User;
      case 'customer':
        return Users;
      case 'supplier':
        return Truck;
      case 'student':
        return User;
      case 'parent':
        return Users;
      case 'employee':
        return Building;
      default:
        return User;
    }
  };

  const getPersonTypeColor = (type) => {
    switch (type) {
      case 'user':
        return 'bg-purple-100 text-purple-800';
      case 'customer':
        return 'bg-blue-100 text-blue-800';
      case 'supplier':
        return 'bg-orange-100 text-orange-800';
      case 'student':
        return 'bg-green-100 text-green-800';
      case 'parent':
        return 'bg-yellow-100 text-yellow-800';
      case 'employee':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatDocument = (document) => {
    if (!document) return '';
    const numbers = document.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (numbers.length === 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return document;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pessoas</h1>
            <p className="text-gray-600">
              Gerencie usuários, clientes, fornecedores e responsáveis
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link to="/dashboard/people/new">
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Pessoa
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Pessoas</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">{stats.active} ativas</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <User className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Usuários</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.users}</p>
                <p className="text-sm text-gray-500">sistema</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Clientes</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.customers}</p>
                <p className="text-sm text-gray-500">ativos</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <Truck className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Fornecedores</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.suppliers}</p>
                <p className="text-sm text-gray-500">cadastrados</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
              <div className="flex-1 max-w-md">
                <Input
                  placeholder="Buscar pessoas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={Search}
                />
              </div>
              
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className={showFilters ? 'bg-blue-50 border-blue-300' : ''}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtros
                </Button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Pessoa
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todos os tipos</option>
                    {personTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Todos os status</option>
                    <option value="active">Ativas</option>
                    <option value="inactive">Inativas</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* People List */}
        <Card>
          <div className="overflow-hidden">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                    Pessoa
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 hidden sm:table-cell">
                    Tipo
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5 hidden lg:table-cell">
                    Contato
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 hidden xl:table-cell">
                    Documento
                  </th>
                  <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                    Status
                  </th>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPeople.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 md:px-6 py-12 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">
                        {searchTerm || selectedType || selectedStatus
                          ? 'Nenhuma pessoa encontrada com os filtros aplicados'
                          : 'Nenhuma pessoa cadastrada ainda'
                        }
                      </p>
                      {!searchTerm && !selectedType && !selectedStatus && (
                        <Link to="/dashboard/people/new">
                          <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
                            <Plus className="w-4 h-4 mr-2" />
                            Cadastrar Primeira Pessoa
                          </Button>
                        </Link>
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredPeople.map((person) => {
                    const TypeIcon = getPersonTypeIcon(person.type);
                    
                    return (
                      <tr key={person.id} className="hover:bg-gray-50">
                        <td className="px-3 md:px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10">
                              <div className={`h-8 w-8 md:h-10 md:w-10 rounded-full flex items-center justify-center ${
                                person.type === 'user' ? 'bg-purple-100' :
                                person.type === 'customer' ? 'bg-blue-100' :
                                person.type === 'supplier' ? 'bg-orange-100' :
                                person.type === 'student' ? 'bg-green-100' :
                                person.type === 'parent' ? 'bg-yellow-100' :
                                person.type === 'employee' ? 'bg-indigo-100' : 'bg-gray-100'
                              }`}>
                                <TypeIcon className={`h-4 w-4 md:h-5 md:w-5 ${
                                  person.type === 'user' ? 'text-purple-600' :
                                  person.type === 'customer' ? 'text-blue-600' :
                                  person.type === 'supplier' ? 'text-orange-600' :
                                  person.type === 'student' ? 'text-green-600' :
                                  person.type === 'parent' ? 'text-yellow-600' :
                                  person.type === 'employee' ? 'text-indigo-600' : 'text-gray-600'
                                }`} />
                              </div>
                            </div>
                            <div className="ml-3 md:ml-4 min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {person.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {person.email}
                              </div>
                              <div className="text-xs text-gray-500 truncate sm:hidden">
                                {getPersonTypeLabel(person.type)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden sm:table-cell">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPersonTypeColor(person.type)}`}>
                            <TypeIcon className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span>{getPersonTypeLabel(person.type)}</span>
                          </span>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden lg:table-cell">
                          <div className="text-sm text-gray-900">
                            {person.phone && (
                              <div className="flex items-center">
                                <Phone className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                                <span>{formatPhone(person.phone)}</span>
                              </div>
                            )}
                            {person.email && (
                              <div className="flex items-center text-xs text-gray-500 mt-1">
                                <Mail className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                                <span className="truncate">{person.email}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4 hidden xl:table-cell">
                          <div className="text-sm text-gray-900">
                            {person.document && (
                              <span className="text-sm text-gray-600">
                                {formatDocument(person.document)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 md:px-4 py-4">
                          <div className="flex items-center">
                            <button
                              onClick={() => handleTogglePersonStatus(person)}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                person.active 
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                            >
                              <span>{person.active ? 'Ativa' : 'Inativa'}</span>
                            </button>
                          </div>
                        </td>
                        <td className="px-2 py-4 text-center">                          <div className="flex items-center justify-center space-x-1">
                            <button 
                              onClick={() => handleShowDetails(person)}
                              title="Ver detalhes"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <Link to={`/dashboard/people/edit/${person.id}`}>
                              <button 
                                title="Editar pessoa"
                                className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors duration-200"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </Link>
                            <button 
                              onClick={() => handleDeletePerson(person)}
                              title="Excluir pessoa"
                              className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>            </table>
          </div>
        </Card>

        {/* Modal de Detalhes da Pessoa */}
        <PersonDetailsModal
          person={selectedPerson}
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          onPersonUpdate={handlePersonUpdate}
          getPersonTypeIcon={getPersonTypeIcon}
          getPersonTypeLabel={getPersonTypeLabel}
          formatPhone={formatPhone}
          formatDocument={formatDocument}
        />
      </div>
    );
  };

export default PeoplePage;